import { Component, OnDestroy, OnInit, ViewEncapsulation, inject } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Subscription, firstValueFrom } from 'rxjs';

import { Events } from 'src/providers/events/events';
import { CustomNavController } from 'src/shared/providers/navigation/custom-nav-controller';
import { RightSidePanelService } from 'src/shared/providers/providers/right-side-panel';
import { TidyStorage } from 'src/shared/providers/tidy-storage';
import { WindowService } from 'src/shared/providers/window.service';

import { Vendors } from 'src/providers/vendors/vendors';
import { Invoices } from 'src/providers/invoices/invoices';

import { CUSTOMER_VENDORS_CHANGED_EVENT, Vendor } from 'src/models/vendor.model';
import { Invoice } from 'src/models/invoice.model';

import { EditVendorPage } from 'src/pages/my-pros/edit-vendor/edit-vendor';
import { CreateInvoicePage } from 'src/pages/invoices/create-invoice/create-invoice';

@Component({
  templateUrl: 'vendor-detail.html',
  encapsulation: ViewEncapsulation.None,
  standalone: false,
})
export class VendorDetailPage implements OnInit, OnDestroy {
  private vendors = inject(Vendors);
  private invoicesProvider = inject(Invoices);
  private events = inject(Events);
  private navCtrl = inject(CustomNavController);
  private rightSidePanelService = inject(RightSidePanelService);
  private storage = inject(TidyStorage);
  private route = inject(ActivatedRoute);
  readonly windowService = inject(WindowService);

  vendor: Vendor | null = null;
  vendorId: number | null = null;
  invoices: Invoice[] = [];
  loaded = false;
  errorMessage = '';
  isRightSideContent = false;

  private changedSubscription?: Subscription;

  boundCreateInvoice = this.goToCreateInvoice.bind(this);
  boundEdit = this.goToEdit.bind(this);

  async ngOnInit() {
    this.isRightSideContent = (await this.storage.retrieve('dialog-right-side-open')) || false;
    const dialogParams = this.isRightSideContent ? await this.storage.retrieve('dialog-params') : null;
    this.vendorId = this.navCtrl.getParam('vendorId') ||
      Number(this.route.snapshot.paramMap.get('vendorId')) ||
      dialogParams?.vendorId || null;
    if (this.isRightSideContent) {
      this.rightSidePanelService.setDialogPageTitle('Vendor');
    }
    this.changedSubscription = this.events.subscribe(CUSTOMER_VENDORS_CHANGED_EVENT, () => this.load());
    await this.load();
  }

  ngOnDestroy(): void {
    this.changedSubscription?.unsubscribe();
  }

  private async load() {
    if (!this.vendorId) {
      this.loaded = true;
      return;
    }
    try {
      this.vendor = await firstValueFrom(this.vendors.getVendor(this.vendorId));
      await this.loadInvoices();
    } catch (err: any) {
      this.errorMessage = err?.error?.message || err?.message || 'Failed to load vendor';
    }
    this.loaded = true;
  }

  private async loadInvoices() {
    try {
      const response = await this.invoicesProvider.getAllInvoicesPaginated({});
      const all = (response?.body || []) as Invoice[];
      this.invoices = all.filter(invoice =>
        invoice?.seller?.type === 'Vendor' && invoice?.seller?.id === this.vendorId
      );
    } catch {
      this.invoices = [];
    }
  }

  async goToEdit() {
    if (!this.vendor) { return; }
    const params = { mode: 'edit', vendor: this.vendor };
    if (this.windowService.isDesktopRes) {
      await this.storage.save('dialog-right-side-open', true);
      await this.storage.save('dialog-params', params);
      this.rightSidePanelService.navigateTo('edit-vendor', params, EditVendorPage);
    } else {
      this.navCtrl.navigateForward('edit-vendor', params);
    }
  }

  async goToCreateInvoice() {
    if (!this.vendor) { return; }
    const params = { vendor: this.vendor };
    if (this.windowService.isDesktopRes) {
      await this.storage.save('dialog-right-side-open', true);
      await this.storage.save('dialog-params', params);
      this.rightSidePanelService.navigateTo('create-invoice', params, CreateInvoicePage);
    } else {
      this.navCtrl.navigateForward('create-invoice', params);
    }
  }
}
